import * as Sentry from '@sentry/react';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import { OpenNewWindow, WarningCircle } from 'iconoir-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { updateHubSpotPrivateAppToken } from '../services/IntegrationsService';

interface Props {
  open: boolean;
  onClose: () => void;
  connectedAccountId: string;
  accountName: string;
  onSuccess?: () => void;
}

const HUBSPOT_PRIVATE_APPS_DOCS =
  'https://developers.hubspot.com/docs/api/private-apps';

/**
 * Modal for updating a HubSpot Private App token
 * Used when the user regenerates their token in HubSpot
 */
export default function UpdateHubSpotPrivateAppTokenModal({
  open,
  onClose,
  connectedAccountId,
  accountName,
  onSuccess,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { show } = useSnackbarContext();

  const [accessToken, setAccessToken] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string>();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setAccessToken('');
      setUpdating(false);
      setError(undefined);
    }
  }, [open]);

  const handleUpdate = useCallback(async () => {
    if (!selectedAccount) {
      setError('No account selected');
      return;
    }

    if (!accessToken.trim()) {
      setError('Please enter your new Private App access token');
      return;
    }

    setUpdating(true);
    setError(undefined);

    try {
      await updateHubSpotPrivateAppToken(
        selectedAccount.id,
        connectedAccountId,
        accessToken.trim(),
      );

      show('Token updated successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to update Private App token:', err);
      Sentry.captureException(err);

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update token';

      // Provide user-friendly error messages
      if (
        errorMessage.includes('Invalid or revoked') ||
        errorMessage.includes('401')
      ) {
        setError(
          'Invalid or revoked token. Please check that your new Private App token is correct.',
        );
      } else if (errorMessage.includes('different HubSpot portal')) {
        setError(
          'This token belongs to a different HubSpot portal. Please use a token from the same portal.',
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setUpdating(false);
    }
  }, [
    selectedAccount,
    connectedAccountId,
    accessToken,
    show,
    onSuccess,
    onClose,
  ]);

  const actions: ModalActionData[] = [
    {
      title: 'Cancel',
      onClick: onClose,
      type: 'secondary',
      disabled: updating,
    },
    {
      title: updating ? 'Updating...' : 'Update Token',
      onClick: handleUpdate,
      type: 'primary',
      disabled: updating || !accessToken.trim(),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update Private App Token"
      description={`Update the access token for "${accountName}"`}
      actions={actions}
    >
      <div className="flex flex-col gap-5">
        {/* Warning banner */}
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <WarningCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Token Rotation</p>
            <p className="mt-1 text-amber-700">
              If you&apos;ve regenerated your Private App token in HubSpot,
              enter the new token here. The old token will be replaced.{' '}
              <a
                href={HUBSPOT_PRIVATE_APPS_DOCS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-amber-600 underline hover:text-amber-800"
              >
                Manage Private Apps
                <OpenNewWindow className="h-3.5 w-3.5" />
              </a>
            </p>
          </div>
        </div>

        {/* Access Token field */}
        <div>
          <FieldTitle title="New Private App Access Token *" />
          <Input
            type="password"
            placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            disabled={updating}
          />
          <p className="mt-1 text-xs text-gray-500">
            Your new Private App access token starting with &quot;pat-&quot;
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
